/**
 * Unified Deals page — "what's becoming real?"
 *
 * Company-centric view that collapses Deal Pipeline + Deal Briefs +
 * Deal Rooms into a single surface. One entry per company with active
 * deal-pipeline rows, expandable to show every interested investor and
 * their stage, match, and brief link.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, FileText, FolderLock, Layers,
  Loader2, Search, Sparkles,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import dealsService, {
  DealsByCompanyPayload,
  DealsEntity,
} from '../services/dealsService';

const STAGE_BADGE_COLOR: Record<string, string> = {
  suggested:        'bg-gray-100 text-gray-700',
  reviewed:         'bg-blue-50 text-blue-700',
  introduced:       'bg-blue-100 text-blue-800',
  meeting_scheduled:'bg-indigo-100 text-indigo-800',
  due_diligence:    'bg-purple-100 text-purple-800',
  term_sheet:       'bg-amber-100 text-amber-800',
  negotiating:      'bg-orange-100 text-orange-800',
  soft_commitment:  'bg-green-50 text-green-700',
  closed:           'bg-green-100 text-green-800',
  passed:           'bg-red-100 text-red-700',
};

const formatStage = (stage: string | null | undefined): string => {
  if (!stage) return '—';
  return stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

interface CompanyCardProps {
  entity: DealsEntity;
}

const CompanyCard: React.FC<CompanyCardProps> = ({ entity }) => {
  const [expanded, setExpanded] = useState<boolean>(true);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <Link
              to={entity.kind === 'company' ? `/companies/${entity.entity_id}` : `/projects/${entity.entity_id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-base font-semibold text-gray-900 hover:text-blue-700 truncate block"
            >
              {entity.entity_name}
            </Link>
            <p className="text-xs text-gray-500 mt-0.5">
              {[entity.industry, entity.funding_stage].filter(Boolean).join(' · ') || entity.kind}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
          {entity.deal_room_id && (
            <Link
              to={`/deal-rooms/${entity.deal_room_id}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded text-xs font-medium text-amber-800 hover:bg-amber-100"
              title="Open the deal room"
            >
              <FolderLock className="w-3.5 h-3.5" /> Room
            </Link>
          )}
          <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
            {entity.investor_count} investor{entity.investor_count === 1 ? '' : 's'}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {entity.investors.map((sub) => {
            const stageColor = STAGE_BADGE_COLOR[sub.stage || ''] || 'bg-gray-100 text-gray-700';
            return (
              <div key={sub.pipeline_id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                <div className="min-w-0 flex-1">
                  <Link
                    to={sub.investor_id ? `/investors/${sub.investor_id}` : '#'}
                    className="text-sm font-medium text-gray-900 hover:text-blue-700"
                  >
                    {sub.investor_name || `Investor #${sub.investor_id}`}
                  </Link>
                  {sub.firm_name && (
                    <p className="text-xs text-gray-500 mt-0.5">{sub.firm_name}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${stageColor}`}>
                    {formatStage(sub.stage)}
                  </span>
                  {sub.match_id && (
                    <Link
                      to={`/matches/${sub.match_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 rounded"
                      title="Open the match"
                    >
                      <Sparkles className="w-3 h-3" /> Match
                    </Link>
                  )}
                  {sub.brief_id && (
                    <Link
                      to={`/matches/${sub.match_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50 rounded"
                      title="Open the AI deal brief"
                    >
                      <FileText className="w-3 h-3" /> Brief
                    </Link>
                  )}
                  <Link
                    to={`/deal-pipeline/${sub.pipeline_id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded"
                    title="Open the pipeline detail"
                  >
                    <Layers className="w-3 h-3" /> Pipeline
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DealsPage: React.FC = () => {
  const [payload, setPayload] = useState<DealsByCompanyPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [includePassed, setIncludePassed] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await dealsService.fetchByCompany(includePassed);
        setPayload(data);
      } catch {
        setPayload({ companies: [], projects: [], total_pipelines: 0 });
      }
      setLoading(false);
    };
    load();
  }, [includePassed]);

  const filtered = useMemo(() => {
    if (!payload) return [] as DealsEntity[];
    const q = search.trim().toLowerCase();
    const all: DealsEntity[] = [...(payload.companies || []), ...(payload.projects || [])];
    if (!q) return all;
    return all.filter((e) =>
      e.entity_name.toLowerCase().includes(q) ||
      e.investors.some((i) =>
        (i.investor_name || '').toLowerCase().includes(q) ||
        (i.firm_name || '').toLowerCase().includes(q)
      )
    );
  }, [payload, search]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deals</h1>
            <p className="text-sm text-gray-600 mt-1">
              Company-centric view of every active match, pipeline row, brief, and deal room.
            </p>
          </div>
          <Link
            to="/deals/briefs"
            className="text-sm text-blue-700 hover:underline whitespace-nowrap"
          >
            All deal briefs →
          </Link>
        </div>

        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3">
          <Search className="w-4 h-4 text-gray-400 ml-1" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, investor, or firm…"
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includePassed}
              onChange={(e) => setIncludePassed(e.target.checked)}
              className="rounded"
            />
            Include passed/closed
          </label>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" /> Loading deals…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
            <Layers className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No active deals yet</h2>
            <p className="text-sm text-gray-600 mb-4">
              Once a match is reviewed and promoted to the pipeline, it will show up here grouped by company.
            </p>
            <Link
              to="/matches"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Review matches →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              {filtered.length} compan{filtered.length === 1 ? 'y' : 'ies'} ·{' '}
              {payload?.total_pipelines ?? 0} pipeline row{payload?.total_pipelines === 1 ? '' : 's'}
            </p>
            {filtered.map((entity) => (
              <CompanyCard key={`${entity.kind}-${entity.entity_id}`} entity={entity} />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DealsPage;
